import React from 'react';
import { useNavigate } from 'react-router-dom';

const TermsOfUse: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white">Terms of Use</h1>
                <p className="text-indigo-100 mt-1">Last updated: {new Date().toLocaleDateString()}</p>
              </div>
              <button
                onClick={() => navigate(-1)}
                className="text-white hover:text-indigo-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Document Viewer */}
            <div className="mb-6">
              <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Terms of Use Document</h3>
                <p className="text-gray-600 mb-4">
                  Click the button below to view or download our Terms of Use document.
                </p>
                <div className="space-x-4">
                <a
                  href="/MPBTerms.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View Document
                </a>
                <a
                  href="/MPBTerms.pdf"
                  download="MPBTerms.pdf"
                  className="inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md font-medium transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download
                </a>
                </div>
              </div>
            </div>

            {/* Key Terms Points */}
            <div className="prose max-w-none">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Key Terms Summary</h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-medium text-blue-900 mb-2">Service Description</h3>
                <ul className="text-blue-800 space-y-2">
                  <li>• Music lesson recording and AI-powered analysis</li>
                  <li>• Practice session tracking and goal setting</li>
                  <li>• Progress monitoring and analytics</li>
                  <li>• Social features for students and teachers</li>
                  <li>• Educational content and resources</li>
                </ul>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-medium text-green-900 mb-2">User Responsibilities</h3>
                <ul className="text-green-800 space-y-2">
                  <li>• Provide accurate information during registration</li>
                  <li>• Use the service for educational purposes only</li>
                  <li>• Respect other users and maintain appropriate conduct</li>
                  <li>• Keep login credentials secure</li>
                  <li>• Follow all applicable laws and regulations</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-medium text-yellow-900 mb-2">Age Requirements</h3>
                <ul className="text-yellow-800 space-y-2">
                  <li>• Users under 13 require parental consent (COPPA compliance)</li>
                  <li>• Parents must verify their identity via email</li>
                  <li>• Parents can manage their child's account and data</li>
                  <li>• Age verification is required during registration</li>
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-medium text-red-900 mb-2">Prohibited Activities</h3>
                <ul className="text-red-800 space-y-2">
                  <li>• Sharing inappropriate or harmful content</li>
                  <li>• Attempting to hack or compromise the service</li>
                  <li>• Impersonating other users or entities</li>
                  <li>• Violating intellectual property rights</li>
                  <li>• Using the service for commercial purposes without permission</li>
                </ul>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-purple-900 mb-2">Service Availability</h3>
                <p className="text-purple-800 mb-2">
                  We strive to maintain high service availability but cannot guarantee uninterrupted access. 
                  The service may be temporarily unavailable for maintenance or updates.
                </p>
                <div className="mt-2 text-purple-800">
                  <p><strong>Support:</strong> mail@mindfulpracticebuddy.com.au</p>
                  <p><strong>Legal:</strong> mail@mindfulpracticebuddy.com.au</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  These terms are effective as of {new Date().toLocaleDateString()}
                </p>
                <button
                  onClick={() => navigate(-1)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
